import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import router from 'next/router';

import { Caption, Container, Link } from '@/atoms';
import { SearchBoxLocations } from '@/molecules';
import { CardsPagination, HousingCard } from '@/organisms';

import { gtmPush } from '@/utils';

import type { FunctionComponent } from 'react';
import type { TypeGridLocationProps } from './types';

/**
 * GridLocation
 */
export const GridLocation: FunctionComponent<TypeGridLocationProps> = ({
  ancestor,
  buildings,
  description,
  pretitle,
  title,
  searchBox,
  ...props
}: TypeGridLocationProps) => {
  const [selectedProvince, setSelectedProvince] = useState(
    searchBox.provinces.defaultValue
  );

  const [abailablesCities, setAbailableCities] = useState({
    cities: searchBox.cities.citiesList.find((city: any) =>
      city.includes(selectedProvince?.id ?? -1)
    ),
    ...(searchBox.cities.defaultValue
      ? { defaultValue: searchBox.cities.defaultValue }
      : { placeholder: searchBox.cities.placeholder }),
  });

  useEffect(() => {
    // Esto se hace para evitar recargar la web cuando entramos a la poblacion directamente
    // Sino al cambiar la provicnia la primera vez recargarria y nos redigiria a la provincia.
    if (searchBox.provinces.defaultValue !== selectedProvince) {
      gtmPush({
        event: 'booking_search',
        provincia: selectedProvince?.label ?? '',
        edificio: '',
        tipo: '',
        fecha: '',
      });
      router.push(selectedProvince?.href ?? '#');
    }

    setAbailableCities({
      cities: searchBox.cities.citiesList.find((city: any) =>
        city.includes(selectedProvince?.id ?? -1)
      ),
      ...(searchBox.cities.defaultValue
        ? { defaultValue: searchBox.cities.defaultValue }
        : { placeholder: searchBox.cities.placeholder }),
    });
  }, [selectedProvince]);

  const onCitiesChanges = (city: any) => {
    gtmPush({
      event: 'booking_search',
      provincia: selectedProvince?.label ?? '',
      edificio: city.label,
    });
    router.push(city.href);
  };

  const onProvincesChanges = (value: any) => {
    setSelectedProvince(
      searchBox.provinces.values.find((province) => province.id === value.id)
    );
  };

  useEffect(() => {
    setAbailableCities({
      cities: searchBox.cities.citiesList.find((city: any) =>
        city.includes(selectedProvince?.id ?? -1)
      ),
      ...(searchBox.cities.defaultValue
        ? { defaultValue: searchBox.cities.defaultValue }
        : { placeholder: searchBox.cities.placeholder }),
    });
  }, [searchBox]);

  return (
    <Container
      as="section"
      className="!pb-6 !pt-20 md:!pb-20 md:!pt-40"
      backgroundColorTop="surface-01"
      {...props}
    >
      <div className="mb-9 flex flex-col md:mb-16">
        <Caption className="mb-4">{pretitle}</Caption>

        <h1 className="text-heading-mobile--h1 max-w-xl md:text-heading--h1">
          {title}
        </h1>

        <div className="my-8 grid max-w-xl grid-cols-1 gap-4 md:my-10 md:grid-cols-2 md:gap-8">
          <SearchBoxLocations
            onProvincesChange={onProvincesChanges}
            onCitiesChange={onCitiesChanges}
            //eslint-disable-next-line
            //@ts-ignore
            cities={abailablesCities}
            provinces={searchBox.provinces}
          />
        </div>

        {ancestor && (
          <NextLink href={ancestor.href} prefetch={false} passHref>
            <Link variant="primary" underline className="mb-8">
              {ancestor.label}
            </Link>
          </NextLink>
        )}

        <p className="text-body--primary font-thin md:text-heading--h5">
          {description}
        </p>
      </div>

      <CardsPagination>
        {buildings.map((building, index) => (
          <HousingCard
            {...building}
            province={selectedProvince}
            characteristicsOrientation="column"
            className="h-full"
            key={index}
          />
        ))}
      </CardsPagination>
    </Container>
  );
};

GridLocation.displayName = 'GridLocation';
